/*
Rules describing how Proto-Indo-European phonemes changed in various branches.
Assumes laryngeals (h₁, h₂, and h₃)
https://en.wikipedia.org/wiki/Indo-European_sound_laws
*/

import { SegmentStrategy } from "../rules/intoSegments";
import { RuleSet } from "../rules/ruleSet";

export const sanskritRules: RuleSet = {
  preprocess: ['cleanPunctuation'],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.Single,
  rules: [
    // vowels & syllabic consonants
    { from: 'h₁', to: 'i', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₁', to: 'i', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₂', to: 'i', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₂', to: 'i', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₃', to: 'i', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₃', to: 'i', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₁', to: 'i', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'h₂', to: 'i', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'h₃', to: 'i', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'ei', to: 'ai' },
    { from: 'oi', to: 'ai' },
    { from: 'ei', to: 'ai', previous: ['laryngeal'] },
    { from: 'ēi', to: 'ai' },
    { from: 'ōi', to: 'ai' },
    { from: 'eh₂ei', to: 'ai' },
    { from: 'eu', to: 'au' },
    { from: 'ou', to: 'au' },
    { from: 'eu', to: 'au', previous: ['laryngeal'] },
    { from: 'ēu', to: 'āu' },
    { from: 'ōu', to: 'āu' },
    { from: 'e', to: 'a', previous: ['laryngeal'] },
    { from: 'e', to: 'ā', next: ['laryngeal'] },
    { from: 'e', to: 'a' },
    { from: 'ē', to: 'ā' },
    { from: 'o', to: 'a' },
    { from: 'ō', to: 'ā' },
    { from: 'i', to: 'ī', next: ['laryngeal'] },
    { from: 'i', to: 'i' },
    { from: 'u', to: 'ū', next: ['laryngeal'] },
    { from: 'u', to: 'u' },
    { from: 'm̥m', to: 'am' },
    { from: 'm̥', to: 'a' },
    { from: 'm', to: 'ā', next: ['laryngeal'] },
    { from: 'n̥n', to: 'an' },
    { from: 'n̥', to: 'a' },
    { from: 'n', to: 'ā', next: ['laryngeal'] },
    { from: 'l̥l', to: 'ir' },
    { from: 'l̥', to: 'ṛ' },
    { from: 'l', to: 'īr', next: ['laryngeal'] },
    { from: 'r̥r', to: 'ir' },
    { from: 'r̥', to: 'ṛ' },
    { from: 'r', to: 'īr', next: ['laryngeal'] },

    // consonant clusters
    { from: 'st', to: 'ṣṭ', previous: 'r' },
    { from: 'st', to: 'ṣṭ', previous: 'u' },
    { from: 'st', to: 'ṣṭ', previous: 'k' },
    { from: 'st', to: 'ṣṭ', previous: 'i' },
    { from: 'st', to: 'st' },
    { from: 'sḱ', to: 'cch', previous: ['vowel'], next: ['vowel'] },
    { from: 'sḱ', to: 't͡ɕʰ' },
    { from: 'tt', to: 'tth', next: ['laryngeal'] },
    { from: 'tt', to: 'tt' },
    { from: 'ḱt', to: 'ʂʈ' },
    { from: 'ḱs', to: 'kṣ' },
    { from: 'ks', to: 'kṣ', previous: 'r' },
    { from: 'ks', to: 'kṣ', previous: 'u' },
    { from: 'ks', to: 'kṣ', previous: 'k' },
    { from: 'ks', to: 'kṣ', previous: 'i' },
    { from: 'kʷs', to: 'kṣ', previous: 'r' },
    { from: 'kʷs', to: 'kṣ', previous: 'u' },
    { from: 'kʷs', to: 'kṣ', previous: 'k' },
    { from: 'kʷs', to: 'kṣ', previous: 'i' },
    { from: 'sd', to: 'ḍ', previous: 'r' },
    { from: 'sd', to: 'ḍ', previous: 'u' },
    { from: 'sd', to: 'ḍ', previous: 'k' },
    { from: 'sd', to: 'ḍ', previous: 'i' },
    { from: 'sd', to: 'd' },
    { from: 'sdʰ', to: 'ḍh', previous: 'r' },
    { from: 'sdʰ', to: 'ḍh', previous: 'u' },
    { from: 'sdʰ', to: 'ḍh', previous: 'k' },
    { from: 'sdʰ', to: 'ḍh', previous: 'i' },
    { from: 'sdʰ', to: 'dh' },

    // consonants
    { from: 'p', to: 'pʰ', next: 'h₂' },
    { from: 'p', to: 'p' },
    { from: 't', to: 'tʰ', next: 'h₂' },
    { from: 't', to: 't' },
    { from: 'ḱ', to: 'ɕ' },
    { from: 'k', to: 'c', next: ['vowel', 'front'] },
    { from: 'k', to: 'kʰ', next: 'h₂' },
    { from: 'k', to: 'k' },
    { from: 'kʷ', to: 'c', next: ['vowel', 'front'] },
    { from: 'kʷ', to: 'kʰ', next: 'h₂' },
    { from: 'kʷ', to: 'k' },
    { from: 'b', to: 'bh', next: 'h₂' },
    { from: 'b', to: 'b' },
    { from: 'd', to: 'dh', next: 'h₂' },
    { from: 'd', to: 'd' },
    { from: 'ǵ', to: 'h', next: 'h₂' },
    { from: 'ǵ', to: 'j' },
    { from: 'g', to: 'j', next: ['vowel', 'front'] },
    { from: 'g', to: 'gh', next: 'h₂' },
    { from: 'g', to: 'g' },
    { from: 'gʷ', to: 'j', next: ['vowel', 'front'] },
    { from: 'gʷ', to: 'gh', next: 'h₂' },
    { from: 'gʷ', to: 'g' },
    { from: 'bʰ', to: 'bʱ' },
    { from: 'dʰ', to: 'dʰ' },
    { from: 'ǵʰ', to: 'h' },
    { from: 'gʰ', to: 'gʰ', next: ['vowel', 'front'] },
    { from: 'gʰ', to: 'gʰ' },
    { from: 'gʷʰ', to: 'gʰ', next: ['vowel', 'front'] },
    { from: 'gʷʰ', to: 'gʰ' },
    { from: 's', to: 's' },
    { from: 'm', to: 'm' },
    { from: 'n', to: 'n' },
    { from: 'l', to: 'r' },
    { from: 'r', to: 'r' },
    { from: 'y', to: 'y' },
    { from: 'w', to: 'v' },

    { from: 'h₁', to: '' },
    { from: 'h₂', to: '' },
    { from: 'h₃', to: '' },
    { from: 'hₓ', to: '' },
  ]
};

export const oldChurchSlavonicRules: RuleSet = {
  preprocess: ['cleanPunctuation'],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.Single,
  rules: [
    // vowels & syllabic consonants
    // note: 'ь' was a short front vowel represented here as 'ă'
    //  and 'ъ' was a short middle rounded vowel represented here as 'ə̆'
    { from: 'h₁e', to: 'e' },
    { from: 'h₂e', to: 'o' },
    { from: 'h₃e', to: 'o' },
    { from: 'h₁', to: '', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₁', to: '', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₂', to: '', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₂', to: '', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₃', to: '', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₃', to: '', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₁', to: '', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'h₂', to: '', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'h₃', to: '', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'eh₂ei', to: 'ě' },
    { from: 'eh₁', to: 'ě' },
    { from: 'eh₂', to: 'a' },
    { from: 'eh₃', to: 'a' },
    { from: 'h₁ei', to: 'i' },
    { from: 'h₂ei', to: 'ě' },
    { from: 'h₃ei', to: 'ě' },
    { from: 'h₁eu', to: 'ju' },
    { from: 'h₂eu', to: 'u' },
    { from: 'h₃eu', to: 'u' },
    { from: 'ei', to: 'i' },
    { from: 'oi', to: 'ě' },
    { from: 'ēi', to: 'i' },
    { from: 'ōi', to: 'y' },
    { from: 'eu', to: 'ju' },
    { from: 'ou', to: 'u' },
    { from: 'ēu', to: 'u' },
    { from: 'e', to: 'e' },
    { from: 'ē', to: 'ě' },
    { from: 'i', to: 'i', next: ['laryngeal'] },
    { from: 'i', to: 'ă' },
    { from: 'o', to: 'o' },
    { from: 'ō', to: 'a' },
    { from: 'u', to: 'y', next: ['laryngeal'] },
    { from: 'u', to: 'ə̆' },
    { from: 'm̥m', to: 'ăm' },
    { from: 'm̥', to: 'ę' },
    { from: 'm', to: 'ę', next: ['laryngeal'] },
    { from: 'n̥n', to: 'ăn' },
    { from: 'n̥', to: 'ę' },
    { from: 'n', to: 'ę', next: ['laryngeal'] },
    { from: 'l̥l', to: 'ăl' },
    { from: 'l̥', to: 'lă' },
    { from: 'l', to: 'lă', next: ['laryngeal'] },
    { from: 'r̥r', to: 'ăr' },
    { from: 'r̥', to: 'ră' },
    { from: 'r', to: 'ră', next: ['laryngeal'] },

    // consonant clusters
    { from: 'sr', to: 'str' },
    { from: 'sḱ', to: 'sk' },
    { from: 'tt', to: 'st' },
    { from: 'pt', to: 't' },
    { from: 'ḱt', to: 'st' },
    { from: 'kt', to: 't' },
    { from: 'kʷt', to: 't' },
    { from: 'sd', to: 'zd' },
    { from: 'sdʰ', to: 'zd' },

    // consonants
    { from: 'p', to: 'p' },
    { from: 't', to: 't' },
    { from: 'ḱ', to: 's' },
    { from: 'k', to: 't͡ʃ', next: ['vowel', 'front'] },
    { from: 'k', to: 'k' },
    { from: 'kʷ', to: 't͡ʃ', next: ['vowel', 'front'] },
    { from: 'kʷ', to: 'k' },
    { from: 'b', to: 'b' },
    { from: 'd', to: 'd' },
    { from: 'ǵ', to: 'z' },
    { from: 'g', to: 'ʒ', next: ['vowel', 'front'] },
    { from: 'g', to: 'g' },
    { from: 'gʷ', to: 'ʒ', next: ['vowel', 'front'] },
    { from: 'gʷ', to: 'g' },
    { from: 'bʰ', to: 'b' },
    { from: 'dʰ', to: 'd' },
    { from: 'ǵʰ', to: 'z' },
    { from: 'gʰ', to: 'ʒ', next: ['vowel', 'front'] },
    { from: 'gʰ', to: 'g' },
    { from: 'gʷʰ', to: 'ʒ', next: ['vowel', 'front'] },
    { from: 'gʷʰ', to: 'g' },
    { from: 's', to: 'x', previous: 'r' },
    { from: 's', to: 'x', previous: 'u' },
    { from: 's', to: 'x', previous: 'k' },
    { from: 's', to: 'x', previous: 'i' },
    { from: 's', to: 's' },
    { from: 'm', to: 'm' },
    { from: 'n', to: 'n' },
    { from: 'l', to: 'l' },
    { from: 'r', to: 'r' },
    { from: 'y', to: 'j' },
    { from: 'w', to: 'v' },

    { from: 'h₁', to: '' },
    { from: 'h₂', to: '' },
    { from: 'h₃', to: '' },
    { from: 'hₓ', to: '' },
  ]
};

export const hittiteRules: RuleSet = {
  preprocess: ['cleanPunctuation'],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.Single,
  rules: [
    // vowels & syllabic consonants
    // note: this set of rules may need to be reordered
    { from: 'h₁e', to: 'e' },
    { from: 'h₂e', to: 'ha' },
    { from: 'h₃e', to: 'a' },
    { from: 'h₁', to: '', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₁', to: '', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₂', to: 'h', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₂', to: 'h', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₃', to: '', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₃', to: '', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₁', to: 'a', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'h₂', to: 'ha', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'h₃', to: 'a', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'eh₁', to: 'ē' },
    { from: 'eh₂', to: 'a' },
    { from: 'eh₃', to: 'a' },
    { from: 'ih₁', to: 'i' },
    { from: 'ih₂', to: 'i' },
    { from: 'ih₃', to: 'i' },
    { from: 'ei', to: 'ē' },
    { from: 'h₁ei', to: 'ē' },
    { from: 'oi', to: 'ē' },
    { from: 'h₃ei', to: 'ē' },
    { from: 'h₂ei', to: 'ē' },
    { from: 'ēi', to: 'ē' },
    { from: 'ōi', to: 'ai' },
    { from: 'eh₂ei', to: 'ai' },
    { from: 'uh₁', to: 'u' },
    { from: 'uh₂', to: 'u' },
    { from: 'uh₃', to: 'u' },
    { from: 'eu', to: 'u' },
    { from: 'h₁eu', to: 'u' },
    { from: 'ou', to: 'u' },
    { from: 'h₃eu', to: 'u' },
    { from: 'h₂eu', to: 'u' },
    { from: 'ēu', to: 'u' },
    { from: 'ōu', to: 'au' },
    { from: 'e', to: 'e' },
    { from: 'o', to: 'a' },
    { from: 'ē', to: 'ē' },
    { from: 'ō', to: 'a' },
    { from: 'i', to: 'i' },
    { from: 'u', to: 'u' },
    { from: 'm̥m', to: 'a' },
    { from: 'm̥', to: 'm' },
    { from: 'mh₁', to: 'm' },
    { from: 'mh₂', to: 'm' },
    { from: 'mh₃', to: 'm' },
    { from: 'n̥n', to: 'n' },
    { from: 'n̥', to: 'an' },
    { from: 'nh₁', to: 'n' },
    { from: 'nh₂', to: 'n' },
    { from: 'nh₃', to: 'n' },
    { from: 'l̥l', to: 'l' },
    { from: 'l̥', to: 'al' },
    { from: 'lh₁', to: 'l' },
    { from: 'lh₂', to: 'l' },
    { from: 'lh₃', to: 'l' },
    { from: 'r̥r', to: 'r' },
    { from: 'r̥', to: 'ar' },
    { from: 'rh₁', to: 'r' },
    { from: 'rh₂', to: 'r' },
    { from: 'rh₃', to: 'r' },

    // consonant clusters
    { from: 'sr', to: 'ʃʃr' },
    { from: 'tw', to: 'ttu' },
    { from: 'dʰw', to: 'tu' },
    { from: 'dw', to: 'tu' },
    { from: 'tt', to: 'zt' },

    // consonants
    { from: 'p', to: 'p' },
    { from: 't', to: 't' },
    { from: 'ḱ', to: 'k' },
    { from: 'k', to: 'k' },
    { from: 'kʷ', to: 'ku' },
    { from: 'b', to: 'p' },
    { from: 'd', to: 't' },
    { from: 'ǵ', to: 'k' },
    { from: 'g', to: 'k' },
    { from: 'gʷ', to: 'ku' },
    { from: 'bʰ', to: 'p' },
    { from: 'dʰ', to: 't' },
    { from: 'ǵʰ', to: 'k' },
    { from: 'gʰ', to: 'k' },
    { from: 'gʷʰ', to: 'ku' },
    { from: 's', to: 's' },
    { from: 'm', to: 'n', next: ['#word'] },
    { from: 'm', to: 'm' },
    { from: 'n', to: 'n' },
    { from: 'l', to: 'l' },
    { from: 'r', to: 'r' },
    { from: 'y', to: 'j' },
    { from: 'w', to: 'w' },

    // remaining laryngeals go away
    { from: 'h₁', to: '' },
    { from: 'h₂', to: '' },
    { from: 'h₃', to: '' },
    { from: 'hₓ', to: '' },
  ]
};

export const pieToEnglish: RuleSet = {
  preprocess: ['cleanPunctuation'],
  inputSegments: SegmentStrategy.Single,
  outputSegments: SegmentStrategy.Single,
  rules: [
    // vowels & syllabic consonants
    // note: this just uses a simplified set of 'normal' rules, not 'umlauted'
    { from: 'h₁e', to: 'eo', next: 'h' },
    { from: 'h₁e', to: 'eo', next: 'w' },
    { from: 'h₁e', to: 'e' },
    { from: 'h₂e', to: 'ea', next: 'h' },
    { from: 'h₂e', to: 'ea', next: 'w' },
    { from: 'h₂e', to: 'æ' },
    { from: 'h₃e', to: 'ea', next: 'h' },
    { from: 'h₃e', to: 'ea', next: 'w' },
    { from: 'h₃e', to: 'æ' },
    { from: 'h₁', to: 'æ', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₁', to: 'æ', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₂', to: 'æ', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₂', to: 'æ', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₃', to: 'æ', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₃', to: 'æ', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₁', to: 'æ', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'h₂', to: 'æ', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'h₃', to: 'æ', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'eh₁', to: 'ǣ' },
    { from: 'eh₂', to: 'ō' },
    { from: 'eh₃', to: 'ō' },
    { from: 'ih₁', to: 'ī' },
    { from: 'ih₂', to: 'ī' },
    { from: 'ih₃', to: 'ī' },
    { from: 'ei', to: 'ī' },
    { from: 'h₁ei', to: 'ī' },
    { from: 'oi', to: 'ā' },
    { from: 'h₃ei', to: 'ā' },
    { from: 'h₂ei', to: 'ā' },
    { from: 'ēi', to: 'ī' },
    { from: 'ōi', to: 'ā' },
    { from: 'eh₂ei', to: 'ā' },
    { from: 'uh₁', to: 'ū' },
    { from: 'uh₂', to: 'ū' },
    { from: 'uh₃', to: 'ū' },
    { from: 'eu', to: 'ēo' },
    { from: 'h₁eu', to: 'ēo' },
    { from: 'ou', to: 'ēa' },
    { from: 'h₃eu', to: 'ēa' },
    { from: 'h₂eu', to: 'ēa' },
    { from: 'ēu', to: 'ēo' },
    { from: 'ōu', to: 'ēa' },
    { from: 'e', to: 'eo', next: 'h' },
    { from: 'e', to: 'eo', next: 'w' },
    { from: 'e', to: 'e' },
    { from: 'o', to: 'ea', next: 'h' },
    { from: 'o', to: 'ea', next: 'w' },
    { from: 'o', to: 'æ' },
    { from: 'ē', to: 'ǣ' },
    { from: 'ō', to: 'ō' },
    { from: 'i', to: 'i' },
    { from: 'u', to: 'u' },
    { from: 'm̥m', to: 'um' },
    { from: 'm̥', to: 'um' },
    { from: 'mh₁', to: 'um' },
    { from: 'mh₂', to: 'um' },
    { from: 'mh₃', to: 'um' },
    { from: 'n̥n', to: 'un' },
    { from: 'n̥', to: 'un' },
    { from: 'nh₁', to: 'un' },
    { from: 'nh₂', to: 'un' },
    { from: 'nh₃', to: 'un' },
    { from: 'l̥l', to: 'ul' },
    { from: 'l̥', to: 'ul' },
    { from: 'lh₁', to: 'ul' },
    { from: 'lh₂', to: 'ul' },
    { from: 'lh₃', to: 'ul' },
    { from: 'r̥r', to: 'ur' },
    { from: 'r̥', to: 'ur' },
    { from: 'rh₁', to: 'ur' },
    { from: 'rh₂', to: 'ur' },
    { from: 'rh₃', to: 'ur' },

    // consonant clusters
    { from: 'sr', to: 'str' },
    { from: 'sp', to: 'sp' },
    { from: 'st', to: 'st' },
    { from: 'sḱ', to: 'ʃ' },
    { from: 'sk', to: 'ʃ' },
    { from: 'skʷ', to: 'ʃ' },
    { from: 'tt', to: 'ss' },
    { from: 'pt', to: 'ft' },
    { from: 'ḱt', to: 't' },
    { from: 'kt', to: 't' },
    { from: 'kʷt', to: 't' },
    { from: 'ps', to: 'ps' },
    { from: 'ḱs', to: 'ks' },
    { from: 'ks', to: 'ks' },
    { from: 'kʷs', to: 'ks' },
    { from: 'sd', to: 'st' },
    { from: 'sdʰ', to: 'd' },

    // consonants
    { from: 'p', to: 'v', previous: ['vowel'], next: ['vowel'] },
    { from: 'p', to: 'f' },
    { from: 't', to: 'th' },
    { from: 'ḱ', to: '', previous: ['vowel'], next: ['vowel'] },
    { from: 'ḱ', to: 'h' },
    { from: 'k', to: '', previous: ['vowel'], next: ['vowel'] },
    { from: 'k', to: 'h' },
    { from: 'kʷ', to: 'wh' },
    { from: 'b', to: 'p' },
    { from: 'd', to: 't' },
    { from: 'ǵ', to: 'c' },
    { from: 'g', to: 'c' },
    { from: 'gʷ', to: 'qu' },
    { from: 'bʰ', to: 'v', previous: ['vowel'], next: ['vowel'] },
    { from: 'bʰ', to: 'v', previous: ['vowel'], next: 'l' },
    { from: 'bʰ', to: 'v', previous: ['vowel'], next: 'r' },
    { from: 'bʰ', to: 'v', previous: 'l', next: ['vowel'] },
    { from: 'bʰ', to: 'v', previous: 'r', next: ['vowel'] },
    { from: 'bʰ', to: 'b' },
    { from: 'dʰ', to: 'd' },
    { from: 'ǵʰ', to: 'w', previous: ['vowel'], next: ['vowel'] },
    { from: 'ǵʰ', to: 'w', previous: ['vowel'], next: 'l' },
    { from: 'ǵʰ', to: 'w', previous: ['vowel'], next: 'r' },
    { from: 'ǵʰ', to: 'w', previous: 'l', next: ['vowel'] },
    { from: 'ǵʰ', to: 'w', previous: 'r', next: ['vowel'] },
    { from: 'ǵʰ', to: 'g' },
    { from: 'gʰ', to: 'w', previous: ['vowel'], next: ['vowel'] },
    { from: 'gʰ', to: 'w', previous: ['vowel'], next: 'l' },
    { from: 'gʰ', to: 'w', previous: ['vowel'], next: 'r' },
    { from: 'gʰ', to: 'w', previous: 'l', next: ['vowel'] },
    { from: 'gʰ', to: 'w', previous: 'r', next: ['vowel'] },
    { from: 'gʰ', to: 'g' },
    { from: 'gʷʰ', to: 'b', previous: ['#word'] },
    { from: 'gʷʰ', to: 'w', previous: ['vowel'], next: ['vowel'] },
    { from: 'gʷʰ', to: 'g' },
    { from: 's', to: 's' },
    { from: 'm', to: '', next: ['#word'] },
    { from: 'm', to: 'm' },
    { from: 'n', to: 'n' },
    { from: 'l', to: 'l' },
    { from: 'r', to: 'r' },
    { from: 'y', to: 'y' },
    { from: 'w', to: 'w' },

    // remaining laryngeals go away
    { from: 'h₁', to: '' },
    { from: 'h₂', to: '' },
    { from: 'h₃', to: '' },
    { from: 'hₓ', to: '' },
  ]
};

/** PIE-to-old-language rules */
export const pieRules: Record<string, RuleSet> = {
  'Sanskrit': sanskritRules,
  'Old Church Slavonic': oldChurchSlavonicRules,
  'Hittite': hittiteRules,
  'English': pieToEnglish,
}

/*
export const PIE: RuleSet = {
  preprocess: ['cleanPunctuation'],
  rules: [
    // vowels & syllabic consonants
    // note: this set of rules may need to be reordered
    { from: 'h₁e', to: '' },
    { from: 'h₂e', to: '' },
    { from: 'h₃e', to: '' },
    { from: 'e', to: '' },
    { from: 'o', to: '' },
    { from: 'h₁', to: '', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₁', to: '', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₂', to: '', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₂', to: '', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₃', to: '', previous: { not: ':vowel' }, next: { not: ':vowel' } },
    { from: 'h₃', to: '', previous: { not: ':vowel' }, next: ['#word'] },
    { from: 'h₁', to: '', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'h₂', to: '', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'h₃', to: '', previous: ['#word'], next: { not: ':vowel' } },
    { from: 'ē', to: '' },
    { from: 'eh₁', to: '' },
    { from: 'eh₂', to: '' },
    { from: 'eh₃', to: '' },
    { from: 'ō', to: '' },
    { from: 'i', to: '' },
    { from: 'ih₁', to: '' },
    { from: 'ih₂', to: '' },
    { from: 'ih₃', to: '' },
    { from: 'ei', to: '' },
    { from: 'h₁ei', to: '' },
    { from: 'oi', to: '' },
    { from: 'h₃ei', to: '' },
    { from: 'h₂ei', to: '' },
    { from: 'ēi', to: '' },
    { from: 'ōi', to: '' },
    { from: 'eh₂ei', to: '' },
    { from: 'u', to: '' },
    { from: 'uh₁', to: '' },
    { from: 'uh₂', to: '' },
    { from: 'uh₃', to: '' },
    { from: 'eu', to: '' },
    { from: 'h₁eu', to: '' },
    { from: 'ou', to: '' },
    { from: 'h₃eu', to: '' },
    { from: 'h₂eu', to: '' },
    { from: 'ēu', to: '' },
    { from: 'ōu', to: '' },
    { from: 'm̥m', to: '' },
    { from: 'm̥', to: '' },
    { from: 'mh₁', to: '' },
    { from: 'mh₂', to: '' },
    { from: 'mh₃', to: '' },
    { from: 'n̥n', to: '' },
    { from: 'n̥', to: '' },
    { from: 'nh₁', to: '' },
    { from: 'nh₂', to: '' },
    { from: 'nh₃', to: '' },
    { from: 'l̥l', to: '' },
    { from: 'l̥', to: '' },
    { from: 'lh₁', to: '' },
    { from: 'lh₂', to: '' },
    { from: 'lh₃', to: '' },
    { from: 'r̥r', to: '' },
    { from: 'r̥', to: '' },
    { from: 'rh₁', to: '' },
    { from: 'rh₂', to: '' },
    { from: 'rh₃', to: '' },

    // consonant clusters
    { from: 'sr', to: '' },
    { from: 'tw', to: '' },
    { from: 'dʰw', to: '' },
    { from: 'dw', to: '' },
    { from: 'ḱw', to: '' },
    { from: 'ǵʰw', to: '' },
    { from: 'ǵw', to: '' },
    { from: 'sw', to: '' },
    { from: 'sp', to: '' },
    { from: 'st', to: '' },
    { from: 'sḱ', to: '' },
    { from: 'sk', to: '' },
    { from: 'skʷ', to: '' },
    { from: 'tt', to: '' },
    { from: 'pt', to: '' },
    { from: 'ḱt', to: '' },
    { from: 'kt', to: '' },
    { from: 'kʷt', to: '' },
    { from: 'ps', to: '' },
    { from: 'ḱs', to: '' },
    { from: 'ks', to: '' },
    { from: 'kʷs', to: '' },
    { from: 'sd', to: '' },
    { from: 'sdʰ', to: '' },

    // consonants
    { from: 'p', to: '' },
    { from: 't', to: '' },
    { from: 'ḱ', to: '' },
    { from: 'k', to: '' },
    { from: 'kʷ', to: '' },
    { from: 'b', to: '' },
    { from: 'd', to: '' },
    { from: 'ǵ', to: '' },
    { from: 'g', to: '' },
    { from: 'gʷ', to: '' },
    { from: 'bʰ', to: '' },
    { from: 'dʰ', to: '' },
    { from: 'ǵʰ', to: '' },
    { from: 'gʰ', to: '' },
    { from: 'gʷʰ', to: '' },
    { from: 's', to: '' },
    { from: 'm', to: '', next: ['#word'] },
    { from: 'm', to: '' },
    { from: 'n', to: '' },
    { from: 'l', to: '' },
    { from: 'r', to: '' },
    { from: 'y', to: '' },
    { from: 'w', to: '' },

    // remaining laryngeals go away
    { from: 'h₁', to: '' },
    { from: 'h₂', to: '' },
    { from: 'h₃', to: '' },
    { from: 'hₓ', to: '' },
  ]
};
*/
